// TESSERAX — REGISTRE NUMÉRIQUE TOLÉRANT (cœur pur, testé).
//
// Plusieurs systèmes de « meilleure marque » persistaient EXACTEMENT le même blob :
// un objet JSON { clé: nombre } stocké en localStorage, relu de façon tolérante, et
// interrogé pour la meilleure valeur connue. `records.parseRecords` (record par mode)
// et `peaks.parsePeaks` (sommet par dimension) en étaient deux copies au caractère près
// — même parse défensif, même `bestFor`/`peakFor`, même `serialize`. On factorise ici
// CE socle commun (sans rien changer au comportement) ; chaque domaine garde son type
// et sa sémantique propres en délégant à ces primitives. Une duplication en moins, un
// seul endroit à durcir.
//
// Module PUR : ni THREE ni DOM, pas de Math.random, pas d'I/O. L'appelant gère la
// persistance localStorage ; ici on ne fait que parser / comparer / sérialiser.

// Un registre = { clé → meilleure valeur (entier > 0) }.
export type NumRegistry = Record<string, number>;

// Parse tolérant d'un blob localStorage : JSON objet { clé: nombre }. Toute valeur non
// finie, négative, nulle ou non numérique est ignorée silencieusement (dégradation
// gracieuse : une entrée corrompue ne casse pas le registre entier). Renvoie toujours
// un objet NEUF et propre, jamais null.
export function parseNumReg(raw: string | null | undefined): NumRegistry {
  const out: NumRegistry = {};
  if (!raw) return out;
  let obj: unknown;
  try { obj = JSON.parse(raw); } catch { return out; }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return out;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    // N'accepter QUE nombre ou chaîne numérique. `Number([123])` vaut 123 et `Number(true)`
    // vaut 1 → un blob corrompu ({combo:[123]} / {combo:true}) ne doit pas devenir un record.
    let n: number;
    if (typeof v === 'number') n = v;
    else if (typeof v === 'string') n = Number(v);
    else continue;
    if (Number.isFinite(n) && n > 0) out[k] = Math.floor(n);
  }
  return out;
}

export function serializeNumReg(r: NumRegistry): string {
  return JSON.stringify(r);
}

// Meilleure valeur connue pour une clé (0 si absente / invalide).
export function bestIn(r: NumRegistry, key: string): number {
  const v = r[key];
  return Number.isFinite(v) && v > 0 ? v : 0;
}
